import { useCallback, useEffect, useRef, useState } from 'react';
import { preferenceKeyForUser } from 'common/constants/userPreferences';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import {
  getPreference,
  migrateLegacyMultiUserPreference,
  setPreference,
  subscribePreferenceChanges,
} from 'services/storage/preferencesIndexedDB';

const useLocalStorageByUser = <T,>(
  key: string,
  initialValue: T
): readonly [T, (value: T) => void, boolean] => {
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const userId = user?.id as number | undefined;
  const storageKey = userId !== undefined ? preferenceKeyForUser(key, userId) : undefined;

  // Capture the initial value once so inline [] / {} literals don't invalidate the effect.
  const initialValueRef = useRef(initialValue);

  const [valueByUser, setValueByUser] = useState<T>(initialValue);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const iv = initialValueRef.current;

    if (userId === undefined || storageKey === undefined) {
      setValueByUser(iv);
      setIsReady(false);
      return;
    }

    let cancelled = false;
    setIsReady(false);

    const load = async () => {
      try {
        const migrated = await migrateLegacyMultiUserPreference(key, storageKey, userId);
        if (cancelled) return;
        if (migrated !== undefined) {
          setValueByUser(migrated as T);
          setIsReady(true);
          return;
        }
        const stored = await getPreference<T>(storageKey);
        if (!cancelled) {
          setValueByUser(stored !== undefined ? stored : iv);
          setIsReady(true);
        }
      } catch {
        if (!cancelled) {
          setValueByUser(iv);
          setIsReady(true);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [key, storageKey, userId]); // initialValue intentionally excluded — captured via ref

  useEffect(() => {
    if (!storageKey) return undefined;
    return subscribePreferenceChanges(({ key: changedKey, value }) => {
      if (changedKey === storageKey) {
        setValueByUser(value as T);
      }
    });
  }, [storageKey]);

  const setValue = useCallback(
    (value: T) => {
      if (userId === undefined || storageKey === undefined) {
        return;
      }
      setValueByUser(value);
      setIsReady(true);
      setPreference(storageKey, value).catch(() => {
        // Errors logged in setPreference callers via useLocalStorage; by-user uses direct API
      });
    },
    [storageKey, userId]
  );

  return [valueByUser, setValue, isReady] as const;
};

export default useLocalStorageByUser;
